/**
 * Custom hook for real-time sync between browser tabs using BroadcastChannel API
 * Enables data synchronization when edits are made in one tab
 */
import { useEffect, useRef, useCallback } from 'react';

const CHANNEL_NAME = 'drayly-sync';

export const useBroadcastSync = (onMessage) => {
  const channelRef = useRef(null);
  // Keep the latest onMessage in a ref so we can install the channel ONCE.
  // Without this, every parent re-render (e.g. typing in an EditLoadSheet
  // input) torn-down and re-created the channel — racing with deliveries and
  // dropping messages that arrived during the gap.
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('BroadcastChannel not supported in this browser');
      return;
    }

    channelRef.current = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current.onmessage = (event) => {
      const fn = onMessageRef.current;
      if (fn && event.data) fn(event.data);
    };

    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, []);

  // Broadcast message to other tabs
  const broadcast = useCallback((type, payload) => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type, payload, timestamp: Date.now() });
    }
  }, []);

  return { broadcast };
};

// Message types for different sync events
export const SYNC_TYPES = {
  SHIPMENT_UPDATED: 'SHIPMENT_UPDATED',
  SHIPMENT_CREATED: 'SHIPMENT_CREATED',
  SHIPMENT_DELETED: 'SHIPMENT_DELETED',
  CELL_COLOR_UPDATED: 'CELL_COLOR_UPDATED',
  STATUS_UPDATED: 'STATUS_UPDATED',
  REFRESH_ALL: 'REFRESH_ALL'
};

export default useBroadcastSync;
